import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { selectCancellationCandidates } from 'store/subscriptions/selectors';
import { selectCurrentUser } from 'store/user/selectors';

import { CANCEL_SUBSCRIPTION_SCREEN_ID } from 'constants/analytics';
import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { sendAnalyticCancelOfferClick } from 'services/Analytics';
import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';

import { useSelector } from 'hooks';
import useModals from 'modals/hooks';

import { isMainSubscription } from 'helpers/subscriptions';
import { getValuesByKey } from 'helpers/utils';

import { CustomPrimaryButton } from 'components/Buttons';
import OutlinedButton from 'components/Buttons/OutlinedButton';

import { CancelOfferAction } from 'types/subscription';

import { checkDiscountAllowed } from '../../hooks/helpers';
import useCancelSubscriptionFlow from '../../hooks/useCancelSubscriptionFlow';
import { useGetTexts } from '../hooks/useGetTexts';

import BaseConfirmationModal from './components/BaseConfirmationModal';

const ConfirmationModalWithDiscount = () => {
    const { t } = useTranslation();

    const { closeModal } = useModals();

    const { country } = useSelector(selectCurrentUser);

    const { onCancelSubscriptionWithOffer, onCancelSubscription } = useCancelSubscriptionFlow();

    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const isMultiSubs = cancellationCandidates && cancellationCandidates?.length > 1;

    const { title, subtitle } = useGetTexts({
        titleText: isMultiSubs
            ? 'subscription.confirmationModalWithDiscount.title.multi'
            : 'subscription.confirmationModalWithDiscount.title.single',
        subtitleText: isMultiSubs
            ? 'subscription.confirmationModalWithDiscount.subtitle.multi'
            : 'subscription.confirmationModalWithDiscount.subtitle.single',
    });

    useEffect(() => {
        if (subscription_id) {
            trackScreenLoad(CANCEL_SUBSCRIPTION_SCREEN_ID, {
                subscription_id,
            });
        }
    }, [subscription_id]);

    const onSubmit = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Cancel,
            eventLabel: { subscription_id },
        });

        if (cancellationCandidates?.[0]) {
            const {
                product_type,
                discount: { discount_type },
            } = cancellationCandidates[0];

            isMainSubscription(product_type) && discount_type !== DISCOUNT_TYPES.extra && checkDiscountAllowed(country)
                ? onCancelSubscriptionWithOffer()
                : onCancelSubscription();
        }
    };

    const onBackButtonClick = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Close,
            eventLabel: { subscription_id },
        });

        closeModal();
    };

    return (
        <BaseConfirmationModal
            title={title}
            subtitle={subtitle}
            onBackButtonClick={onBackButtonClick}
            dataTestId="confirmationModalWithDiscount"
        >
            <CustomPrimaryButton dataLocator="cancelBtn" onClick={onSubmit} mb={8}>
                {t('basics.button.cancelSubscription')}
            </CustomPrimaryButton>
            <OutlinedButton
                dataLocator="goBackBtn"
                onClick={onBackButtonClick}
                text={t('basics.button.goBack')}
                backgroundColor="surface-main"
                mb={4}
            />
        </BaseConfirmationModal>
    );
};

export default ConfirmationModalWithDiscount;
